'use strict';
(() => {
  if (window.IdolnestMotion) return;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const root = document.documentElement;
  const cards = new WeakSet();
  const images = new WeakSet();
  const observedCards = new Set();
  const imageCleanups = new Map();
  const running = new Map();
  const cardSelector = '[data-in-card-layout], [data-in-catalog-item]';
  const imageSelector = '.product-image-wrap img, .in-card-image img, [data-in-media] img';
  const ease = 'cubic-bezier(.22,1,.36,1)';
  const constrained = () => !!connection?.saveData || /^(slow-2g|2g)$/.test(connection?.effectiveType || '') ||
    (navigator.deviceMemory > 0 && navigator.deviceMemory <= 2) ||
    (navigator.hardwareConcurrency > 0 && navigator.hardwareConcurrency <= 2);
  const quiet = () => reduced.matches || constrained() || document.hidden;
  const syncMode = () => { root.dataset.inMotionMode = quiet() ? 'quiet' : 'full'; };
  const visible = node => node.isConnected && !node.hidden && node.getClientRects().length > 0;
  const find = (scope, selector) => !scope?.querySelectorAll ? [] :
    [...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector)];

  function animate(node, frames, options) {
    if (quiet() || !node.animate || !visible(node)) return;
    const animation = node.animate(frames, {duration:300, easing:ease, ...options});
    running.set(animation, node);
    animation.finished.then(() => running.delete(animation), () => running.delete(animation));
    return animation;
  }

  function cancelAnimations(scope) {
    for (const [animation, node] of running) {
      if (!scope || scope === node || scope.contains?.(node)) {
        animation.cancel();
        running.delete(animation);
      }
    }
  }

  function reveal(nodes) {
    let index = 0;
    if (quiet()) return;
    for (const node of nodes) {
      if (!visible(node)) continue;
      const rect = node.getBoundingClientRect();
      if (rect.right <= 0 || rect.left >= innerWidth || rect.bottom <= 0 || rect.top >= innerHeight) continue;
      cancelAnimations(node);
      const delay = Math.min(index++, 4) * 30;
      // Leave the card's transform to its existing hover styles. Only the inner copy moves.
      animate(node, [{opacity:.65}, {opacity:1}], {duration:260, delay});
      const copy = node.querySelector('.in-studio-card__body') || node.querySelector('.product-name');
      if (copy) animate(copy, [
        {transform:'translate3d(0,6px,0)'},
        {transform:'translate3d(0,0,0)'}
      ], {duration:300, delay});
    }
  }

  const cardObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    const entering = entries.filter(entry => entry.isIntersecting && visible(entry.target));
    for (const entry of entries) {
      if (!entry.target.isConnected || entering.includes(entry)) {
        cardObserver.unobserve(entry.target);
        observedCards.delete(entry.target);
      }
    }
    reveal(entering.map(entry => entry.target));
  }, {threshold:.08}) : null;

  const imageObserver = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
    for (const entry of entries) entry.target.classList.toggle('in-motion-image-visible', entry.isIntersecting);
  }, {rootMargin:'80px'}) : null;

  function watchImage(img) {
    if (images.has(img)) return;
    images.add(img);
    const frame = img.closest('.product-image-wrap, .in-card-image, [data-in-media]');
    const primary = frame?.matches('[data-in-media].is-active') ||
      img.fetchPriority === 'high' || img.loading !== 'lazy';
    // The primary gallery image and eager/LCP images never wait for motion or decoding.
    if (!frame || img.complete || primary || quiet()) return;
    let settled = false;
    const finish = event => {
      if (settled) return;
      settled = true;
      clearTimeout(placeholderTimer);
      clearTimeout(watchdog);
      img.removeEventListener('load', finish);
      img.removeEventListener('error', finish);
      imageObserver?.unobserve(frame);
      frame.classList.remove('in-motion-image-pending', 'in-motion-image-visible');
      imageCleanups.delete(img);
      if (event?.type === 'load' && img.naturalWidth > 0 && !quiet() &&
          img.getBoundingClientRect().top > innerHeight * .5) {
        animate(img, [{opacity:.78}, {opacity:1}], {duration:180});
      }
    };
    // Cached/fast images do not flash a skeleton between two paints.
    const placeholderTimer = setTimeout(() => {
      if (settled || img.complete || !frame.isConnected || quiet()) return;
      frame.classList.add('in-motion-image-pending');
      imageObserver?.observe(frame);
      if (!imageObserver) frame.classList.add('in-motion-image-visible');
    }, 140);
    // A failed or indefinitely slow image must never leave a permanent loading layer.
    const watchdog = setTimeout(finish, 12000);
    imageCleanups.set(img, () => finish());
    img.addEventListener('load', finish, {once:true});
    img.addEventListener('error', finish, {once:true});
    if (img.complete) finish();
  }

  function cleanImages(scope) {
    for (const [img, cleanup] of imageCleanups) if (!scope || scope === img || scope.contains?.(img)) cleanup();
  }

  function init(scope = document, initial = false) {
    syncMode();
    for (const node of observedCards) if (!node.isConnected) {
      cardObserver?.unobserve(node);
      observedCards.delete(node);
    }
    for (const [img, cleanup] of imageCleanups) if (!img.isConnected) cleanup();
    for (const node of find(scope, cardSelector)) {
      if (cards.has(node)) continue;
      cards.add(node);
      if (quiet()) continue;
      const rect = node.getBoundingClientRect();
      // First-view merchandise is visible immediately, including a paginated entry page.
      if (initial && visible(node) && rect.top < innerHeight && rect.bottom > 0) continue;
      cardObserver?.observe(node);
      if (cardObserver) observedCards.add(node);
    }
    for (const img of find(scope, imageSelector)) watchImage(img);
  }

  document.addEventListener('idolnest:catalog:rendered', event => {
    const grid = event.detail?.grid;
    if (!grid) return;
    init(grid);
    if (!event.detail.loading && event.detail.changed) {
      const nodes = [...grid.querySelectorAll(cardSelector)].filter(visible);
      for (const node of nodes) {
        cardObserver?.unobserve(node);
        observedCards.delete(node);
      }
      reveal(nodes);
    }
  });
  document.addEventListener('idolnest:cards:added', event => init(event.detail?.scope || document));
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    cleanImages(event.target);
    cancelAnimations(event.target);
    for (const node of find(event.target, cardSelector)) {
      cardObserver?.unobserve(node);
      observedCards.delete(node);
    }
  });

  function environmentChanged() {
    syncMode();
    if (quiet()) {
      cancelAnimations();
      cleanImages();
    }
  }
  if (reduced.addEventListener) reduced.addEventListener('change', environmentChanged);
  else reduced.addListener?.(environmentChanged);
  connection?.addEventListener?.('change', environmentChanged);
  document.addEventListener('visibilitychange', environmentChanged);
  window.addEventListener('pagehide', () => {
    cancelAnimations();
    cleanImages();
  });
  window.addEventListener('pageshow', () => {
    cancelAnimations();
    init(document, true);
  });
  window.IdolnestMotion = {init};
  init(document, true);
})();

